/**
 * Offline weryfikator hasła administratora.
 * Ten sam wzorzec PBKDF2 co offlineAuth.ts, osobne klucze.
 */

const VERIFIER_KEY = "cached_admin_password_verifier";
const SALT_KEY = "cached_admin_password_salt";
const PBKDF2_ITERATIONS = 200_000;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function createSalt(): string {
  return bytesToBase64(crypto.getRandomValues(new Uint8Array(16)));
}

async function deriveVerifier(password: string, saltB64: string): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error("WebCrypto unavailable");
  }

  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, [
    "deriveBits",
  ]);

  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: base64ToBytes(saltB64),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    256,
  );

  return bytesToBase64(new Uint8Array(bits));
}

/**
 * Zapisz weryfikator po udanym użyciu hasła admina online.
 * Wywołać z każdej mutacji z hasłem, która zwróciła 2xx.
 */
export async function storeAdminPasswordVerifier(password: string): Promise<void> {
  try {
    const salt = createSalt();
    const verifier = await deriveVerifier(password, salt);
    localStorage.setItem(SALT_KEY, salt);
    localStorage.setItem(VERIFIER_KEY, verifier);
    console.log("[offlineAdminPassword] Verifier stored");
  } catch (error) {
    console.error("[offlineAdminPassword] Failed to store verifier:", error);
  }
}

/**
 * Sprawdź, czy mamy weryfikator (czy kiedykolwiek użyto hasła admina online).
 */
export function hasAdminPasswordVerifier(): boolean {
  try {
    return Boolean(
      localStorage.getItem(SALT_KEY) && localStorage.getItem(VERIFIER_KEY),
    );
  } catch {
    return false;
  }
}

export type VerifyResult =
  | { ok: true }
  | { ok: false; reason: "no_verifier" | "mismatch" | "unsupported" };

/**
 * Zweryfikuj hasło offline.
 * - no_verifier: nigdy nie użyto hasła admina online na tym urządzeniu
 * - mismatch: hasło błędne
 * - unsupported: brak WebCrypto
 */
export async function verifyAdminPasswordOffline(password: string): Promise<VerifyResult> {
  const savedSalt = localStorage.getItem(SALT_KEY);
  const savedVerifier = localStorage.getItem(VERIFIER_KEY);

  if (!savedSalt || !savedVerifier) {
    return { ok: false, reason: "no_verifier" };
  }

  try {
    const candidate = await deriveVerifier(password, savedSalt);
    if (candidate === savedVerifier) {
      return { ok: true };
    }
    return { ok: false, reason: "mismatch" };
  } catch {
    return { ok: false, reason: "unsupported" };
  }
}

/**
 * Komunikat błędu weryfikacji hasła offline.
 */
export function formatOfflineVerifyError(result: VerifyResult): string {
  if (result.ok) {
    return "";
  }
  if (result.reason === "no_verifier") {
    return "Najpierw potwierdź hasło administratora przy sieci (np. w Ustawieniach), aby używać go offline.";
  }
  if (result.reason === "mismatch") {
    return "Nieprawidłowe hasło administratora.";
  }
  return "Nie można zweryfikować hasła offline (brak WebCrypto).";
}

export async function requireOfflineAdminPassword(
  password: string | null | undefined,
): Promise<string | null> {
  if (!password || navigator.onLine) {
    return null;
  }
  const verified = await verifyAdminPasswordOffline(password);
  return verified.ok ? null : formatOfflineVerifyError(verified);
}

/**
 * Wyczyść weryfikator (np. po zmianie hasła admina w ustawieniach).
 */
export function clearAdminPasswordVerifier(): void {
  try {
    localStorage.removeItem(SALT_KEY);
    localStorage.removeItem(VERIFIER_KEY);
    console.log("[offlineAdminPassword] Verifier cleared");
  } catch {
    /* ignore */
  }
}
